import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const cardVariants = cva(
  'bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-200',
  {
    variants: {
      variant: {
        default: '',
        selectable: 'cursor-pointer hover:shadow-md hover:border-primary-300 hover:scale-[1.01] active:scale-[0.99]',
        elevated: 'shadow-lg border-gray-100',
        outlined: 'border-2 border-gray-300 shadow-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

interface CardProps extends React.HTMLAttributes<HTMLDivElement>,
                            VariantProps<typeof cardVariants> {
  onClick?: () => void;
  selected?: boolean;
}

const Card = ({ className, variant, onClick, selected, children, ...props }: CardProps) => {
  return (
    <div
      role={variant === 'selectable' ? 'button' : undefined}
      tabIndex={variant === 'selectable' ? 0 : undefined}
      onClick={onClick}
      onKeyDown={variant === 'selectable' ? (e) => e.key === 'Enter' && onClick?.() : undefined}
      className={cn(
        cardVariants({ variant }),
        selected && 'border-primary-500 ring-2 ring-primary-200',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const CardHeader = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-6 py-4 border-b border-gray-100', className)} {...props}>
    {children}
  </div>
);

const CardContent = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-6 py-4', className)} {...props}>
    {children}
  </div>
);

const CardFooter = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-6 py-4 border-t border-gray-100', className)} {...props}>
    {children}
  </div>
);

const CardTitle = ({ className, children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3 className={cn('text-lg font-semibold text-gray-900', className)} {...props}>
    {children}
  </h3>
);

const CardSubtitle = ({ className, children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-gray-500 mt-1', className)} {...props}>
    {children}
  </p>
);

const CardDescription = ({ className, children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-gray-600 mt-2', className)} {...props}>
    {children}
  </p>
);

Card.Header = CardHeader;
Card.Content = CardContent;
Card.Footer = CardFooter;
Card.Title = CardTitle;
Card.Subtitle = CardSubtitle;
Card.Description = CardDescription;

export { Card };